/* eslint-disable import/no-duplicates */
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import ptBr from 'date-fns/locale/pt-BR';
import { MdCheck, MdClose, MdGavel, MdSearch, MdSend } from 'react-icons/md';
import { ReactComponent as NoteMoney } from 'assets/images/noteMoney.svg';
import { RiPencilFill } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { StepClaimStatus } from 'store/modules/claims/types';
import { ApplicationState } from 'store/store.inteface';
import { STEPS_STATUS, STATUS_OBJ, BROKER_ACTIONS } from 'utils/statusEnum';
import { useHistoryWithHost } from 'routes/useHistoryWithHost';
import { useWindowSize } from 'hooks';

export const useClaimsStatus = () => {
  const history = useHistoryWithHost();
  const { windowSize } = useWindowSize();

  const timelineBreakPoints = useMemo<{ [key: string]: boolean }>(
    () => ({
      2: !!(windowSize.width && windowSize.width > 0 && windowSize.width < 400),
      3: !!(
        windowSize.width &&
        windowSize.width >= 400 &&
        windowSize.width < 500
      ),
      4: !!(
        windowSize.width &&
        windowSize.width >= 500 &&
        windowSize.width < 700
      ),
      5: !!(windowSize.width && windowSize.width >= 700),
    }),
    [windowSize.width],
  );

  const claimStatus = useMemo(
    () => ({
      [STEPS_STATUS.CommunicationDate]: {
        icon: MdSend,
        color: 'normal',
      },
      [STEPS_STATUS.Documentation]: {
        icon: MdSend,
        color: 'normal',
      },
      [STEPS_STATUS.SuccessLossCalculation]: {
        icon: MdSend,
        color: 'normal',
      },
      [STEPS_STATUS.SignedTerm]: {
        icon: MdSend,
        color: 'normal',
      },
      [STEPS_STATUS.DocumentsSent]: {
        icon: MdSearch,
        color: 'warning',
      },
      [STEPS_STATUS.FailedLossCalculation]: {
        icon: MdSearch,
        color: 'warning',
      },
      [STEPS_STATUS.CreditSchedule]: {
        icon: MdGavel,
        color: 'warning',
      },
      [STEPS_STATUS.DocumentRequest]: {
        icon: RiPencilFill,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/document-request`),
        color: 'error',
        buttonTitle: 'Preencher e enviar',
      },
      [STEPS_STATUS.LossCalculation]: {
        icon: RiPencilFill,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/loss-calculation`),
        color: 'error',
        buttonTitle: 'Acessar a planilha',
      },
      [STEPS_STATUS.DischargeTerms]: {
        icon: RiPencilFill,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/discharge-terms`),
        color: 'error',
        buttonTitle: 'Acessar o termo',
      },
      [STEPS_STATUS.NegativeLetter]: {
        icon: MdClose,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/negative-letter`),
        color: 'error',
        buttonTitle: 'Ver detalhes >',
      },
      [STEPS_STATUS.AdministrativeClosure]: {
        icon: MdClose,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/administrative-closure`),
        color: 'error',
        buttonTitle: 'Ver detalhes >',
      },
      [STEPS_STATUS.InvalidInformations]: {
        icon: MdClose,
        onButtonClick: (id: string) =>
          history.push(`/claim/${id}/administrative-closure`),
        color: 'error',
        buttonTitle: 'Ver detalhes >',
      },
      [STEPS_STATUS.FullIndemnity]: {
        icon: NoteMoney,
        color: 'success',
      },
      [STEPS_STATUS.PartialIndemnity]: {
        icon: NoteMoney,
        color: 'partial',
      },
      // cenário especial: planilha ainda não disponível (em análise)
      [STATUS_OBJ.Analysis + STEPS_STATUS.LossCalculation]: {
        icon: RiPencilFill,
        color: 'warning',
      },
      // cenário especial: termo ainda não disponível (em análise)
      [STATUS_OBJ.Analysis + STEPS_STATUS.DischargeTerms]: {
        icon: RiPencilFill,
        color: 'warning',
      },
    }),

    [],
  );

  const stepClaimStatus = useSelector(
    (state: ApplicationState) => state.claims.stepsClaimStatus,
  );
  const [mountedClaims, setMountedClaims] = useState<StepClaimStatus[]>([]);

  useEffect(() => {
    if (stepClaimStatus.length > 0) {
      const isPaidProcessPP = stepClaimStatus.find(
        obj => obj.statusBroker === BROKER_ACTIONS.PaidProcessPP,
      );
      let groupsPerPage;
      if (isPaidProcessPP) {
        groupsPerPage = Number(9);
      } else {
        groupsPerPage = Number(5);
      }

      const stepsSize =
        Math.ceil(stepClaimStatus.length / groupsPerPage) * groupsPerPage;

      const stepsIndex = [...Array(stepsSize).keys()];

      const pageStatus = stepsIndex.map(pageIndex => {
        const stepClaim = stepClaimStatus[pageIndex];

        if (!stepClaim?.description || !stepClaim.date) return {};
        const claimStatusResultExcept =
          claimStatus[stepClaim.status + stepClaim.description];

        const stepTimelineClaim = {
          ...stepClaim,
          ...(claimStatusResultExcept ?? claimStatus[stepClaim.description]),
          date: format(new Date(stepClaim.date), 'dd MMM yyyy', {
            locale: ptBr,
          }),
        };
        if (stepClaimStatus && pageIndex === stepClaimStatus.length - 1)
          return stepTimelineClaim;

        return {
          ...stepTimelineClaim,
          icon: MdCheck,
          color: 'normal',
        };
      });

      setMountedClaims(pageStatus);
    }
  }, [stepClaimStatus, claimStatus, timelineBreakPoints]);

  useEffect(() => {
    if (!stepClaimStatus.length) {
      setMountedClaims([]);
    }
  }, [stepClaimStatus]);

  return mountedClaims;
};
